import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Printer, Check, X, RefreshCw, Copy, Download, TestTube, Clock, AlertCircle, Monitor, Wifi, WifiOff } from 'lucide-react';
import api from '../api/axios';

const PRINTER_MODELS = [
  { id: 'generic', label: 'Generique ESC/POS' },
  { id: 'epson-tm-t20', label: 'Epson TM-T20' },
  { id: 'epson-tm-t88', label: 'Epson TM-T88' },
  { id: 'star-tsp143', label: 'Star TSP143' },
  { id: 'xprinter-xp80', label: 'Xprinter XP-80C' },
  { id: 'custom', label: 'Autre (ESC/POS compatible)' },
];

const TICKET_MODES = [
  { id: 'single', label: 'Ticket unique (plats + boissons ensemble)' },
  { id: 'split', label: 'Tickets separes (1 Cuisine + 1 Boissons)' },
  { id: 'both', label: 'Double impression (ticket global + tickets services)' },
  { id: 'per_dish', label: 'Recommande : un ticket par plat + boissons separees' },
];

const FONT_DEFAULTS = {
  font_size_title: 16,
  font_size_ticket_type: 18,
  font_size_table: 22,
  font_size_customer: 16,
  font_size_datetime: 14,
  font_size_body: 16,
  font_size_total: 22,
};
const FONT_SIZES = Array.from({ length: 22 }, (_, index) => index + 11);

const PrinterConfig = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    if (!token) { navigate('/staff/login'); return; }
    fetchConfig();
    fetchJobs();
    const interval = setInterval(() => { fetchConfig(); fetchJobs(); }, 15000);
    return () => clearInterval(interval);
  }, [navigate]);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/printer/config');
      setConfig(res.data);
    } catch { toast({ title: 'Erreur chargement config', variant: 'destructive' }); }
    finally { setLoading(false); }
  };

  const fetchJobs = async () => {
    setJobsLoading(true);
    try {
      const res = await api.get('/printer/jobs?status=all&limit=20');
      setJobs(res.data);
    } catch {}
    finally { setJobsLoading(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await api.put('/printer/config', config);
      toast({ title: 'Configuration sauvegardee' });
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const generateKey = async () => {
    try {
      const res = await api.post('/printer/generate-key');
      setConfig({ ...config, api_key: res.data.api_key });
      toast({ title: 'Cle API generee' });
    } catch { toast({ title: 'Erreur', variant: 'destructive' }); }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(config?.api_key || '');
    toast({ title: 'Cle copiee dans le presse-papier' });
  };

  const testPrint = async () => {
    try {
      await api.post('/printer/test');
      toast({ title: 'Job de test cree', description: 'Le service d\'impression devrait l\'imprimer sous peu.' });
      setTimeout(fetchJobs, 1000);
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur', variant: 'destructive' });
    }
  };

  const reprintJob = async (jobId) => {
    try {
      await api.post(`/printer/jobs/${jobId}/reprint`);
      toast({ title: 'Ticket remis en file d’impression' });
      fetchJobs();
    } catch { toast({ title: 'Réimpression impossible', variant: 'destructive' }); }
  };

  const downloadAgent = async () => {
    try {
      const response = await api.get('/printer/agent/download', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'LaTaverneAgent.exe';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: 'LaTaverneAgent.exe telecharge', description: 'Il est deja configure : lancez-le sur le PC caisse.' });
    } catch (error) {
      const detail = error.response?.status === 400
        ? 'Generez d’abord une cle API.'
        : 'Le fichier agent.exe est indisponible.';
      toast({ title: 'Telechargement impossible', description: detail, variant: 'destructive' });
    }
  };

  const printerNames = (config?.agent_printers || []).map(p => p.name);
  const renderPrinterSelect = (field, label, testId) => (
    <div>
      <label className="text-xs font-semibold text-gray-700 block mb-1">{label}</label>
      <Select value={config[field] || '__none__'} onValueChange={v => setConfig({ ...config, [field]: v === '__none__' ? '' : v })}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Choisir une imprimante" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">— Non assignee —</SelectItem>
          {printerNames.map(name => <SelectItem key={name} value={name}>{name}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const renderFontSizeSelect = (field, label, testId) => (
    <div>
      <label className="text-xs font-semibold text-gray-700 block mb-1">{label}</label>
      <Select value={String(config[field] || FONT_DEFAULTS[field])} onValueChange={value => {
        const size = parseInt(value);
        setConfig({
          ...config,
          [field]: size,
          ...(field === 'font_size_body' ? { font_size: size } : {}),
        });
      }}>
        <SelectTrigger data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          {FONT_SIZES.map(size => <SelectItem key={size} value={String(size)}>{size} pt</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  if (loading || !config) return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;

  const statusColor = {
    pending: 'bg-amber-100 text-amber-700',
    printing: 'bg-blue-100 text-blue-700',
    done: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      <div className="bg-[#3d2817] text-white py-3 shadow-lg">
        <div className="max-w-4xl mx-auto px-4 flex items-center gap-4">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={() => navigate('/staff/dashboard')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <Printer size={22} className="text-amber-300" />
          <div className="flex-1">
            <h1 className="text-lg font-bold">Configuration Imprimante</h1>
            <p className="text-xs text-gray-300">Impression automatique des tickets</p>
          </div>
          <Badge className={`text-xs ${config.enabled ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
            {config.enabled ? 'Active' : 'Desactivee'}
          </Badge>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-5">

        {/* Activation */}
        <Card className="border-2 border-[#d4c4b0]">
          <CardHeader className="bg-[#f5f0ea] pb-2">
            <CardTitle className="text-sm">Activation</CardTitle>
          </CardHeader>
          <CardContent className="py-4">
            <button onClick={() => setConfig({ ...config, enabled: !config.enabled })}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-lg border-2 transition-all ${config.enabled ? 'bg-green-50 border-green-400 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}
              data-testid="enable-toggle">
              <div className={`w-12 h-6 rounded-full transition-all relative ${config.enabled ? 'bg-green-500' : 'bg-gray-300'}`}>
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${config.enabled ? 'left-7' : 'left-1'}`} />
              </div>
              <span className="font-semibold text-sm">
                {config.enabled ? 'Impression automatique ACTIVEE' : 'Impression automatique DESACTIVEE'}
              </span>
            </button>
            <p className="text-[10px] text-gray-500 mt-2">Quand active, chaque commande validee cree automatiquement un job d'impression.</p>
          </CardContent>
        </Card>

        {/* Printer Settings */}
        <Card className="border-2 border-[#d4c4b0]">
          <CardHeader className="bg-[#f5f0ea] pb-2">
            <CardTitle className="text-sm">Parametres de l'imprimante</CardTitle>
          </CardHeader>
          <CardContent className="py-4 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Modele d'imprimante</label>
                <Select value={config.printer_model} onValueChange={v => setConfig({ ...config, printer_model: v })}>
                  <SelectTrigger data-testid="printer-model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRINTER_MODELS.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Mode de ticket</label>
                <Select value={config.ticket_mode} onValueChange={v => setConfig({ ...config, ticket_mode: v })}>
                  <SelectTrigger data-testid="ticket-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TICKET_MODES.map(m => <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {config.ticket_mode === 'per_dish' && (
                  <p className="text-[10px] text-amber-700 mt-1">
                    Chaque quantité de plat génère son propre ticket cuisine. Les boissons restent sur un ticket séparé.
                  </p>
                )}
                {config.ticket_mode === 'both' && (
                  <p className="text-[10px] text-red-700 mt-1 font-semibold">
                    Ce mode duplique volontairement la commande : un ticket global puis les tickets par service.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Largeur papier</label>
                <Select value={String(config.paper_width)} onValueChange={v => setConfig({ ...config, paper_width: parseInt(v) })}>
                  <SelectTrigger data-testid="paper-width"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="80">80mm (standard)</SelectItem>
                    <SelectItem value="58">58mm (petit format)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-4 pt-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={config.auto_cut} onChange={e => setConfig({ ...config, auto_cut: e.target.checked })} className="w-4 h-4 rounded" />
                  <span className="text-xs font-semibold text-gray-700">Decoupe auto</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={config.print_logo !== false} onChange={e => setConfig({ ...config, print_logo: e.target.checked })} className="w-4 h-4 rounded" />
                  <span className="text-xs font-semibold text-gray-700">Logo sur ticket caisse uniquement</span>
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tailles typographiques par zone */}
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardHeader className="bg-blue-50 pb-2">
            <CardTitle className="text-sm">Tailles des textes par zone</CardTitle>
            <p className="text-[10px] text-gray-500">Chaque zone est indépendante, de 11 à 32 pt. Les textes trop longs sont automatiquement réduits pour ne pas être coupés.</p>
          </CardHeader>
          <CardContent className="py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {renderFontSizeSelect('font_size_title', 'Titre La Taverne', 'font-size-title')}
              {renderFontSizeSelect('font_size_ticket_type', 'Type : Cuisine / Boissons', 'font-size-ticket-type')}
              {renderFontSizeSelect('font_size_table', 'Numéro de table', 'font-size-table')}
              {renderFontSizeSelect('font_size_customer', 'Nom du client', 'font-size-customer')}
              {renderFontSizeSelect('font_size_datetime', 'Date et heure', 'font-size-datetime')}
              {renderFontSizeSelect('font_size_body', 'Corps : articles et options', 'font-size-body')}
              {renderFontSizeSelect('font_size_total', 'Total du ticket caisse', 'font-size-total')}
            </div>
          </CardContent>
        </Card>

        {/* Restaurant Info on Ticket */}
        <Card className="border-2 border-[#d4c4b0]">
          <CardHeader className="bg-[#f5f0ea] pb-2">
            <CardTitle className="text-sm">Informations sur le ticket</CardTitle>
          </CardHeader>
          <CardContent className="py-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Nom du restaurant</label>
                <Input value={config.restaurant_name || ''} onChange={e => setConfig({ ...config, restaurant_name: e.target.value })} data-testid="restaurant-name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-700 block mb-1">Telephone</label>
                <Input value={config.restaurant_phone || ''} onChange={e => setConfig({ ...config, restaurant_phone: e.target.value })} data-testid="restaurant-phone" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">Adresse</label>
              <Input value={config.restaurant_address || ''} onChange={e => setConfig({ ...config, restaurant_address: e.target.value })} data-testid="restaurant-address" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-700 block mb-1">Texte de pied de ticket</label>
              <Input value={config.footer_text || ''} onChange={e => setConfig({ ...config, footer_text: e.target.value })} data-testid="footer-text" />
            </div>
          </CardContent>
        </Card>

        {/* Agent Windows */}
        <Card className="border-2 border-purple-200 bg-purple-50/40">
          <CardHeader className="bg-purple-50 pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Monitor size={14} className="text-purple-700" />
              Agent Windows (LaTaverneAgent.exe)
            </CardTitle>
          </CardHeader>
          <CardContent className="py-4 space-y-3">
            <div className="flex items-center gap-3">
              {config.agent_online ? (
                <Badge className="bg-green-500 text-white flex items-center gap-1"><Wifi size={10} /> En ligne</Badge>
              ) : (
                <Badge className="bg-gray-500 text-white flex items-center gap-1"><WifiOff size={10} /> Hors ligne</Badge>
              )}
              {config.agent_hostname && (
                <span className="text-xs text-gray-600">PC: {config.agent_hostname} {config.agent_version && `(v${config.agent_version})`}</span>
              )}
            </div>
            {config.agent_last_seen && (
              <p className="text-[10px] text-gray-500">Dernier contact: {new Date(config.agent_last_seen).toLocaleString('fr-FR', { timeZone: 'America/Cayenne' })}</p>
            )}
            {(config.agent_printers || []).length > 0 ? (
              <div className="bg-white rounded border p-2">
                <p className="text-[10px] font-bold text-gray-700 mb-1">Imprimantes detectees sur le PC caisse :</p>
                {(config.agent_printers || []).map(p => (
                  <div key={p.name} className="text-[10px] text-gray-600 flex items-center gap-1">
                    <Printer size={10} /> {p.name}{p.is_default ? ' (defaut Windows)' : ''}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[10px] text-amber-700">Lancez LaTaverneAgent.exe sur le PC caisse pour detecter les imprimantes installees.</p>
            )}
          </CardContent>
        </Card>

        {/* Assignation imprimantes */}
        <Card className="border-2 border-[#d4c4b0]">
          <CardHeader className="bg-[#f5f0ea] pb-2">
            <CardTitle className="text-sm">Assignation des imprimantes</CardTitle>
          </CardHeader>
          <CardContent className="py-4 space-y-4">
            <p className="text-[10px] text-gray-500">Selectionnez quelle imprimante Windows utilise pour chaque type de ticket. Les commandes clients arrivent sur l'imprimante « Commandes client ».</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {renderPrinterSelect('client_printer', 'Commandes client (QR)', 'client-printer')}
              {renderPrinterSelect('caisse_printer', 'Ticket caisse / addition', 'caisse-printer')}
              {renderPrinterSelect('cuisine_printer', 'Ticket cuisine (mode split)', 'cuisine-printer')}
              {renderPrinterSelect('boissons_printer', 'Ticket boissons (mode split)', 'boissons-printer')}
            </div>
          </CardContent>
        </Card>

        {/* API Key & Agent setup */}
        <Card className="border-2 border-amber-200 bg-amber-50/50">
          <CardHeader className="bg-amber-50 pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><AlertCircle size={14} className="text-amber-600" /> Connexion LaTaverneAgent.exe</CardTitle>
          </CardHeader>
          <CardContent className="py-4 space-y-3">
            <p className="text-[11px] text-gray-600">L'agent Windows affiche son état, reçoit les commandes et démarre automatiquement avec le PC caisse.</p>
            <div className="flex items-center gap-2">
              <Input value={config.api_key || ''} readOnly className="flex-1 font-mono text-xs bg-white" data-testid="api-key-display" />
              <Button onClick={copyKey} variant="outline" size="sm" className="shrink-0" data-testid="copy-key-btn"><Copy size={14} /></Button>
              <Button onClick={generateKey} variant="outline" size="sm" className="shrink-0" data-testid="generate-key-btn"><RefreshCw size={14} /></Button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={downloadAgent} className="bg-[#3d2817] hover:bg-[#2c1810] text-white" data-testid="download-agent-btn">
                <Download size={14} className="mr-1.5" /> Telecharger agent.exe
              </Button>
              <Button onClick={testPrint} variant="outline" data-testid="test-print-btn">
                <TestTube size={14} className="mr-1.5" /> Test d'impression
              </Button>
            </div>
            <div className="bg-white rounded-lg border border-amber-200 p-3 text-[10px] text-gray-600 space-y-1">
              <p className="font-bold text-gray-800">Installation sur le PC caisse :</p>
              <p>1. Generer une cle API si le champ ci-dessus est vide</p>
              <p>2. Cliquer sur <strong>Telecharger agent.exe</strong></p>
              <p>3. Copier <code>LaTaverneAgent.exe</code> sur le PC caisse Windows</p>
              <p>4. Double-cliquer dessus — aucun fichier JSON n'est necessaire</p>
              <p>   L'agent s'installe et s'ajoute automatiquement au démarrage de Windows</p>
              <p>   Les PDF sont conservés sur le serveur. Le fichier Windows temporaire est supprimé automatiquement après 10 minutes.</p>
              <p>5. Revenir ici : les imprimantes Windows apparaissent → assignez-les → Sauvegarder</p>
              <p>6. Chaque commande client imprime automatiquement sous 5 secondes</p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button onClick={saveConfig} disabled={saving} className="w-full bg-[#8B0000] hover:bg-[#6d0000] text-white h-11 text-sm font-bold" data-testid="save-config-btn">
          {saving ? 'Sauvegarde...' : 'Sauvegarder la configuration'}
        </Button>

        {/* Print Jobs Log */}
        <Card className="border-2 border-[#d4c4b0]">
          <CardHeader className="bg-[#f5f0ea] pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm">File d'impression</CardTitle>
            <div className="flex gap-1">
              <Button onClick={fetchJobs} variant="outline" size="sm" className="h-6 text-[10px] px-2"><RefreshCw size={10} className="mr-0.5" /> Actualiser</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {jobs.length === 0 ? (
              <p className="text-center py-6 text-gray-400 text-xs">Aucun job d'impression</p>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                {jobs.map(job => (
                  <div key={job.id} className="flex items-center gap-3 px-4 py-2 text-xs" data-testid={`job-${job.id}`}>
                    <Badge className={`text-[8px] h-4 ${statusColor[job.status] || 'bg-gray-100 text-gray-600'}`}>{job.status}</Badge>
                    <span className="font-semibold">{job.ticket_type}</span>
                    <span className="text-gray-500">T{job.table_number}</span>
                    <span className="text-gray-500">{job.customer_name}</span>
                    <span className="text-gray-400 flex-1 text-right">{job.cayenne_time}</span>
                    {job.retries > 0 && <span className="text-orange-500 text-[9px]">retry {job.retries}</span>}
                    {job.error && <span className="text-red-500 text-[9px] truncate max-w-[100px]" title={job.error}>{job.error}</span>}
                    {job.pdf_available && (
                      <Button onClick={() => reprintJob(job.id)} variant="outline" size="sm" className="h-6 text-[9px] px-2">
                        <RefreshCw size={9} className="mr-1" /> Réimprimer
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default PrinterConfig;
